import { useMemo, useState } from "react";
import { Calculator } from "lucide-react";
import { Reveal, Eyebrow } from "./motion";

function Slider({ label, value, suffix, min, max, step, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span className="text-[var(--slate)]">{label}</span>
        <span className="text-[#0A1128] font-medium">{value}{suffix}</span>
      </div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[var(--gold)] cursor-pointer" />
    </div>
  );
}

export default function MortgageSection() {
  const [currency, setCurrency] = useState("US$");
  const [price, setPrice] = useState(500000);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(25);

  const { monthly, loan } = useMemo(() => {
    const loanAmt = price * (1 - down / 100);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r === 0 ? loanAmt / n : (loanAmt * r) / (1 - Math.pow(1 + r, -n));
    return { monthly: isFinite(m) ? m : 0, loan: loanAmt };
  }, [price, down, rate, years]);

  const fmt = (n) => `${currency}${Math.round(n).toLocaleString("en-US")}`;

  return (
    <section id="calculator" data-testid="mortgage-section" className="bg-[#E8E4D9]/40 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow>Plan Ahead</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-5 text-balance">
              Mortgage <span className="italic text-[var(--gold)]">Calculator</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[var(--slate)] mt-6 max-w-md leading-relaxed">
              Estimate your monthly payment on any Cayman property. Adjust the price, deposit, rate and term to see what fits your plans — then reach out and I&apos;ll connect you with trusted local lenders.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <div className="bg-white border border-[#0A1128]/12 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-8">
              <Calculator size={20} className="text-[var(--gold)]" />
              <span className="font-serif text-2xl text-[#0A1128]">Estimate Your Payment</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Home Price</label>
                <input data-testid="ms-price" type="number" value={price} onChange={(e) => setPrice(Number(e.target.value) || 0)}
                  className="w-full bg-transparent border-b border-[#0A1128]/25 py-2.5 text-[#0A1128] text-lg focus:border-[var(--gold)] focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Currency</label>
                <div className="flex gap-2 mt-2">
                  {["US$", "CI$"].map((cur) => (
                    <button key={cur} data-testid={`ms-cur-${cur === "US$" ? "usd" : "kyd"}`} onClick={() => setCurrency(cur)}
                      className={`px-5 py-2 text-sm border transition-colors duration-300 ${currency === cur ? "border-[var(--gold)] bg-[var(--gold)]/10 text-[#0A1128]" : "border-[#0A1128]/20 text-[var(--slate)] hover:border-[#0A1128]/40"}`}>
                      {cur}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <Slider label="Down Payment" value={down} suffix="%" min={5} max={60} step={5} onChange={setDown} testid="ms-down" />
              <Slider label="Interest Rate" value={rate} suffix="%" min={2} max={12} step={0.25} onChange={setRate} testid="ms-rate" />
              <Slider label="Term" value={years} suffix=" yrs" min={10} max={30} step={5} onChange={setYears} testid="ms-years" />
            </div>

            <div className="mt-8 pt-6 border-t border-[#0A1128]/10 grid grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Loan Amount</div>
                <div className="font-serif text-2xl md:text-3xl text-[#0A1128] mt-1">{fmt(loan)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Est. Monthly</div>
                <div data-testid="ms-monthly" className="font-serif text-2xl md:text-3xl text-[var(--gold)] mt-1">{fmt(monthly)}</div>
              </div>
            </div>
            <p className="text-xs text-[var(--slate)]/70 mt-5 leading-relaxed">Estimate excludes insurance, stamp duty, and fees. For illustration only.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
